// src/@shared/utils/orbHelpers.js
import { pickRandomWalkablePoint } from "./worldSceneHelpers";
import Phaser from "phaser";
import { isWalkable } from "../../@shared/utils/worldSceneHelpers";


export const ORB_FRAMES = { down: 0, left: 1, right: 0, up: 3 };

// tweakable offsets for your glass highlight
export const ORB_GLASS = {
    offsets: {
        [ORB_FRAMES.down]: { x: 0, y: 0 },
        [ORB_FRAMES.left]: { x: -1, y: 0 },
        [ORB_FRAMES.right]: { x: 0, y: 0 },
        [ORB_FRAMES.up]: { x: 0, y: -1 },
    },
};

export const getRandomOrbTint = () => {
    const TINTS = [0xff476f, 0x5bc0eb, 0x9cff6b, 0xffd166, 0xb794f4, 0x2dd4bf];
    return TINTS[Phaser.Math.Between(0, TINTS.length - 1)];
};

export const directionFromVelocity = (vx, vy, dead = 0.05) => {
    const ax = Math.abs(vx), ay = Math.abs(vy);
    if (ax < dead && ay < dead) return null;
    if (ay >= ax) return vy >= 0 ? "down" : "up";
    return vx >= 0 ? "right" : "left";
};

export const getFrameSize = (scene, orb) => {
    const fr = orb.frame || scene.textures.getFrame(orb.texture.key, 0);
    const fw = fr.realWidth ?? fr.width ?? fr.sourceSize?.w ?? fr.texture?.source[0]?.width ?? 64;
    const fh = fr.realHeight ?? fr.height ?? fr.sourceSize?.h ?? fr.texture?.source[0]?.height ?? 64;
    return { fw, fh };
};

export const computeGlassBaseRadius = (scene, orb) => {
    const { fw, fh } = getFrameSize(scene, orb);
    return Math.min(fw, fh) * 0.285; // your tuned ratio
};

export const redrawOrbGlass = (scene, orb, glassCfg = ORB_GLASS) => {
    const g = orb._glass?.gfx;
    if (!g) return;
    const { tint, frameIdx, baseRadius } = orb._glass;
    const off = glassCfg.offsets[frameIdx] || { x: 0, y: 0 };

    g.clear();
    g.fillStyle(tint, 1.0);
    const r = baseRadius * orb.scale;
    g.fillCircle(orb.x + off.x * orb.scale, orb.y + off.y * orb.scale, r);
};

export const startOrbWander = (scene, orb, speed = 0.8) => {
    const choose = () => {
        const ang = Phaser.Math.FloatBetween(0, Math.PI * 2);
        const vx = Math.cos(ang) * speed;
        const vy = Math.sin(ang) * speed;
        orb._lastSafeX = orb.x;
        orb._lastSafeY = orb.y;
        orb.setVelocity(vx, vy);
        const dir = directionFromVelocity(vx, vy);
        if (dir) orb.setFrame(ORB_FRAMES[dir] ?? 0);
        scene.time.delayedCall(Phaser.Math.Between(1500, 3000), choose);
    };
    choose();

    const onHit = (ev) => {
        ev.pairs.forEach(({ bodyA, bodyB }) => {
            if (bodyA === orb.body || bodyB === orb.body) {
                const ang = Phaser.Math.FloatBetween(0, Math.PI * 2);
                const vx = Math.cos(ang) * speed;
                const vy = Math.sin(ang) * speed;
                // orb._lastSafeX = orb.x;
                // orb._lastSafeY = orb.y;
                orb.setVelocity(vx, vy);
                const dir = directionFromVelocity(vx, vy);
                if (dir) orb.setFrame(ORB_FRAMES[dir] ?? 0);
            }
        });
    };
    scene.matter.world.on("collisionstart", onHit);
    orb.once("destroy", () => scene.matter.world.off("collisionstart", onHit));
};

export const spawnWorldOrbs = (scene, npcMap, orbKeys = ["RedOrb", "BlueOrb", "GreenOrb"], count = 3, depth = 400, opts = {}) => {
    const {
        obstacles = [],
        bounds = scene.cameras.main.getBounds(),
        minClearance = 48,
        debug = false,
        player = null,
    } = opts;

    // 🚫 TEMP: disable world orbs while we build new open-world functionality
    return [];

    const orbs = [];
    for (let i = 0; i < count; i++) {
        const cfg = npcMap[orbKeys[i % orbKeys.length]];
        if (!cfg) continue;

        const radius = 20 * (cfg.scale ?? 1);
        const { x, y } = pickRandomWalkablePoint(obstacles, bounds, minClearance, 40, 160, radius);

        const orb = scene.matter.add.sprite(x, y, cfg.key, 0, {
            shape: { type: "circle", radius: Math.max(14, radius) },
            frictionAir: 0.08,
            ignoreGravity: true,
            isSensor: true,
            restitution: 0,
        })
            .setFixedRotation()
            .setDepth(depth)
            .setScale(cfg.scale ?? 1)
            .setOrigin(0.5, 0.5);
        // .setMass(300);

        // tag so collisions can recognize NPC orbs
        orb.setData("isNPCOrb", true);

        // track last safe position (start with spawn)
        orb._lastSafeX = x;
        orb._lastSafeY = y;


        const tint = getRandomOrbTint();
        orb.setTint(tint);

        // glass highlight below ring
        const glassGfx = scene.add.graphics().setDepth(depth - 1);
        glassGfx.setBlendMode(Phaser.BlendModes.SCREEN);
        orb._glass = {
            gfx: glassGfx,
            tint,
            frameIdx: 0,
            baseRadius: computeGlassBaseRadius(scene, orb),
        };
        redrawOrbGlass(scene, orb);

        // initial idle frame
        orb.setFrame(ORB_FRAMES.down ?? 0);

        // initial motion/frame
        const vx = Phaser.Math.FloatBetween(-0.8, 0.8);
        const vy = Phaser.Math.FloatBetween(-0.8, 0.8);
        orb.setVelocity(vx, vy);
        const dir = directionFromVelocity(vx, vy);
        if (dir) orb.setFrame(ORB_FRAMES[dir] ?? 0);

        // choose behavior
        if (cfg.ai === "chaser" && player) {
            startChaserAI(scene, orb, player, obstacles, bounds, cfg);
        } else {
            startOrbWander(scene, orb, cfg.wanderSpeed ?? 0.8);
        }

        if (debug) {
            const boxG = scene.add.graphics().setDepth(depth + 1);
            const circG = scene.add.graphics().setDepth(depth + 1);
            const b = orb.getBounds();
            boxG.fillStyle(0x00ff00, 0.25).fillRect(b.x, b.y, b.width, b.height);
            const r = Math.max(14, radius);
            circG.lineStyle(2, 0xff00ff, 1).strokeCircle(orb.body.position.x, orb.body.position.y, r);
            orb._dbg = { boxG, circG, r };
        }

        orbs.push(orb);
    }
    return orbs;
};

export const startChaserAI = (scene, orb, player, obstacles, bounds, cfg) => {
    const speed = cfg.speed ?? 1.0;
    const aggroRange = cfg.aggroRange ?? 220;
    const giveUpRange = cfg.giveUpRange ?? aggroRange * 1.5;

    let chasing = false;
    let wanderTimer = null;

    const chooseWander = () => {
        const ang = Phaser.Math.FloatBetween(0, Math.PI * 2);
        orb._lastSafeX = orb.x;
        orb._lastSafeY = orb.y;
        orb.setVelocity(Math.cos(ang) * 0.7, Math.sin(ang) * 0.7);
        const dir = directionFromVelocity(orb.body.velocity.x, orb.body.velocity.y);
        if (dir) orb.setFrame(ORB_FRAMES[dir] ?? 0);
        wanderTimer = scene.time.delayedCall(Phaser.Math.Between(1200, 2400), chooseWander);
    };

    // start wandering initially
    chooseWander();

    const updateAI = () => {
        if (!player?.body || !orb?.body) return;

        orb._lastSafeX = orb.x;
        orb._lastSafeY = orb.y;

        const dx = player.x - orb.x;
        const dy = player.y - orb.y;
        const dist = Math.hypot(dx, dy);

        if (!chasing && dist <= aggroRange) {
            chasing = true;
            if (wanderTimer) { wanderTimer.remove(false); wanderTimer = null; }
            // 🔔 tell the scene the player entered aggro
            scene.events.emit("npc-aggro", { orb, player });
        } else if (chasing && dist >= giveUpRange) {
            chasing = false;
            if (!wanderTimer) chooseWander();
        }

        if (chasing) {



            // robust radius for player even if it's a rectangle body
            const pb = player.body?.bounds;
            const prCircle = player.body?.circleRadius;
            const prAabb = pb ? Math.max(pb.max.x - pb.min.x, pb.max.y - pb.min.y) * 0.5 : 18;
            const pr = prCircle ?? prAabb;

            const or = orb.body?.circleRadius ?? 18;

            // distance we must keep so colliders just touch (plus a tiny buffer)
            const stopDist = pr + or + (cfg.stopBuffer ?? 6);

            if (dist <= stopDist) {
                // place the orb right on the rim of the player's collider (no overlap)
                const len = dist || 1e-9;
                const nx = player.x - (dx / len) * stopDist;
                const ny = player.y - (dy / len) * stopDist;
                orb.setPosition(nx, ny);
                orb.setVelocity(0, 0);

                // pick the facing based on current (tiny) velocity or relative vector
                const dir = directionFromVelocity(orb.body.velocity.x, orb.body.velocity.y)
                    || directionFromVelocity(-dx, -dy);
                if (dir) orb.setFrame(ORB_FRAMES[dir] ?? 0);
                return; // ← IMPORTANT: don’t try to move closer this tick
            }

            // // ⛔ stop at the edge of the player collider
            // const pr = player.body.circleRadius ?? 18;
            // const or = orb.body.circleRadius ?? 18;
            // const stopDist = pr + or + (cfg.stopBuffer ?? 4);
            // if (dist <= stopDist) {
            //     orb.setVelocity(0, 0);
            //     return;
            // }

            // // desired velocity toward player
            // const len = dist || 1e-9;
            // const vx = (dx / len) * speed;
            // const vy = (dy / len) * speed;

            // // simple “look-ahead” test to avoid face-planting into walls
            // const look = 26;
            // const nx = orb.x + vx * look;
            // const ny = orb.y + vy * look;

            // if (isWalkable(obstacles, nx, ny, orb.body.circleRadius ?? 18, 18)) {
            //     orb.setVelocity(vx, vy);
            // } else {
            //     // slide by turning a bit left/right
            //     const ang = Math.atan2(dy, dx) + Phaser.Math.FloatBetween(-0.6, 0.6);
            //     orb.setVelocity(Math.cos(ang) * speed * 0.8, Math.sin(ang) * speed * 0.8);
            // }

            // const dir = directionFromVelocity(orb.body.velocity.x, orb.body.velocity.y);
            // if (dir) orb.setFrame(ORB_FRAMES[dir] ?? 0);

        }
    };

    // tie into scene update
    const tick = scene.events.on("update", updateAI);

    // bounce away from collisions (keeps it lively)
    const onHit = (ev) => {
        ev.pairs.forEach(({ bodyA, bodyB }) => {
            if (bodyA === orb.body || bodyB === orb.body) {
                const ang = Phaser.Math.FloatBetween(0, Math.PI * 2);
                orb.setVelocity(Math.cos(ang) * speed, Math.sin(ang) * speed);
            }
        });
    };
    scene.matter.world.on("collisionstart", onHit);

    // cleanup
    orb.once("destroy", () => {
        scene.events.off("update", updateAI, tick);
        scene.matter.world.off("collisionstart", onHit);
        wanderTimer?.remove?.(false);
    });
};


